import { AppError } from "config/errors";
import {
  ApiFormGroup,
  DynamicFormInputItems,
  FlowTypes,
  IFlow,
  IFlowSession,
  IFormInputs,
} from "config/types";
import {
  DynamicFormGroupApiMutationBodyInput,
  dynamicFormGroupMutationBodyMutation,
} from "gql/mutations";
import { createFlowResponseMutation, createFlowSessionMutation } from "gql/mutations/flow";
import { INextFormInput, dynamicFormQuery, IDynamicFormQueryInputs } from "gql/queries/dynamicForm";
import { flowQuery } from "gql/queries/flow";
import {
  FINISH_FLOW,
  FLOW_FIELD_SET,
  FLOW_VALIDATION_ERROR,
  SET_FORM_FLOW_DIRTY,
  SET_FORM_FLOW_INDEX,
} from "store/constants";
import { IAnalyticsTracker } from "store/reducers/analyticsTracker";

import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import { compact, map } from "lodash-es";

/* ------------------------       ACTIONS      ------------------------------ */

export const dismissDynamicFormModal = createAction("dismissDynamicFormModal");

export const finishFlow = createAction<{
  flowTracker: IAnalyticsTracker;
}>(FINISH_FLOW);

export const flowFieldSet = createAction<Record<string, any>>(FLOW_FIELD_SET);

export const flowFieldValidationError = createAction<Record<string, any>>(FLOW_VALIDATION_ERROR);

export const setFormFlowIndex = createAction<"back" | "next">(SET_FORM_FLOW_INDEX);

export const setFormFlowDirty = createAction<boolean>(SET_FORM_FLOW_DIRTY);

/* ------------------------       THUNKS      ------------------------------ */

export const initFormFlow = createAsyncThunk(
  "flow/initFormFlow",
  async (
    { slug, sessionFromStart = false }: { slug: FlowTypes; sessionFromStart?: boolean },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const flow = await dispatch(fetchFlow(slug)).unwrap();

      const flowSession = await dispatch(
        createFlowSession({ flowId: flow.id, fromStart: sessionFromStart })
      ).unwrap();

      return { flowSession, flow };
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  }
);

export const fetchFlow = createAsyncThunk(
  "flow/fetchFlow",
  async (slug: FlowTypes, { rejectWithValue }) => {
    try {
      const { data } = await flowQuery(slug);
      if (!data) {
        throw new Error("No data returned from flowQuery");
      }

      return data.flow;
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  }
);

export const createFlowSession = createAsyncThunk(
  "flow/createFlowSession",
  async (
    { flowId, fromStart }: { flowId: IFlow["id"]; fromStart: boolean },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await createFlowSessionMutation(flowId, fromStart);

      return data.createFlowSession?.flowSession;
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  }
);

export const retryNextForm = createAsyncThunk(
  "flow/retryNextForm",
  async (inputPayload: INextFormInput, { rejectWithValue }) => {
    try {
      const response = await dynamicFormQuery(inputPayload);
      if (!response.data) {
        throw new Error("No data returned from dynamicFormQuery");
      }
      return response.data.dynamicForm;
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  }
);

export const nextDynamicForm = createAsyncThunk(
  "flow/nextForm",
  async (inputPayload: INextFormInput, { rejectWithValue }) => {
    try {
      const { data } = await dynamicFormQuery(inputPayload);
      if (!data) {
        throw new Error("No data returned from dynamicFormQuery");
      }
      return data.dynamicForm;
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  }
);

export const fetchDynamicForm = createAsyncThunk(
  "flow/nextForm",
  async (inputPayload: IDynamicFormQueryInputs, { rejectWithValue }) => {
    try {
      const { data } = await dynamicFormQuery(inputPayload);
      if (!data) {
        throw new Error("No data returned from dynamicFormQuery");
      }
      return data.dynamicForm;
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  }
);

// TODO - remove when 'previous' direction API ready
export const fetchPreviousDynamicForm = createAsyncThunk(
  "flow/nextForm",
  async (inputPayload: Parameters<typeof dynamicFormQuery>[0], { dispatch, rejectWithValue }) => {
    try {
      const { data } = await dynamicFormQuery(inputPayload);
      if (!data) {
        throw new Error("No data returned from dynamicFormQuery");
      }

      // removes 1. current form in stack
      //         2. previous stale form, to replace with updated form from query
      dispatch(setFormFlowIndex("back"));
      dispatch(setFormFlowIndex("back"));

      return data.dynamicForm;
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  }
);

interface IDynamicFormGroupMutationBody extends DynamicFormGroupApiMutationBodyInput {
  mutationKey: ApiFormGroup["detail"]["key"];
}

export const groupDetailMutationBody = createAsyncThunk(
  "dynamicForm/groupDetailMutationBody",
  async ({ inputPayload, mutationBody }: IDynamicFormGroupMutationBody, { rejectWithValue }) => {
    try {
      const { data } = await dynamicFormGroupMutationBodyMutation({
        inputPayload,
        mutationBody,
      });

      return data;
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  }
);

export const createFlowResponse = createAsyncThunk(
  "flow/createFlowResponse",
  async (
    { formInputs, flowSessionId }: { formInputs: IFormInputs; flowSessionId: IFlowSession["id"] },
    { rejectWithValue }
  ) => {
    try {
      const fieldResponses = compact(
        map(formInputs, input => {
          const value = input.value;

          return {
            fieldId: input.metadata?.flowQuestionId || input.id,
            response: {
              inputType: input.metadata.flowInputType as DynamicFormInputItems["type"],
              required: input.required,
              value,
            },
          };
        })
      );

      const { data } = await createFlowResponseMutation(flowSessionId, fieldResponses);

      return data.createResponse || [];
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  }
);
